import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useState, useEffect } from "react";

interface LanguageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLanguageSelect: (language: string) => void;
  selectedLanguage: string | null;
  title?: string;
}

// ✅ Language code → full name map
const languageNameMap: Record<string, string> = {
  en: "English",
  hi: "Hindi",
  fr: "French",
  de: "German",
  ja: "Japanese",
  es: "Spanish",
  zh: "Chinese (Simplified)",
  ru: "Russian",
  ar: "Arabic",
  pt: "Portuguese",
  ko: "Korean",
  it: "Italian",
  tr: "Turkish",
  nl: "Dutch",
  sv: "Swedish",
  vi: "Vietnamese",
  th: "Thai",
  el: "Greek",
  he: "Hebrew",
  pl: "Polish",
  my: "Burmese (Myanmar)",
  mr: "Marathi",
  kn: "Kannada",
  gu: "Gujarati",
  ta: "Tamil",
  te: "Telugu",
  ml: "Malayalam",
  bn: "Bengali",
  pa: "Punjabi",
  or: "Odia",
  as: "Assamese",
  id: "Indonesian",
  ms: "Malay",
  tl: "Filipino",
  ur: "Urdu",
  sw: "Swahili",
  am: "Amharic",
  ha: "Hausa",
  af: "Afrikaans"
};

export function LanguageDialog({
  isOpen,
  onClose,
  onLanguageSelect,
  selectedLanguage,
  title = "Choose Your Language"
}: LanguageDialogProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ✅ Pass code directly to parent
  const handleLanguageSelect = (code: string) => {
    onLanguageSelect(code);
    console.log("Selected language code:", code);
    onClose();
  };

  // ✅ Mobile Fullscreen Dialog
  if (isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none p-0 m-0 bg-white dark:bg-[rgb(10,19,19)]">
          <div className="flex flex-col h-full">
            <DialogHeader className="flex-shrink-0 p-3 border-b border-pink-900 dark:border-gray-700">
              <DialogTitle className="text-gray-900 dark:text-white text-base font-light text-center">
                {title}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-700 dark:scrollbar-track-gray-900">
              <LanguageSelector
                onLanguageSelect={handleLanguageSelect}
                selectedLanguage={selectedLanguage || undefined}
                className="w-full text-sm"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ✅ Desktop Dialog
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md md:max-w-lg lg:max-w-2xl max-h-[85vh] overflow-y-auto bg-white dark:bg-[rgb(10,19,19)] border border-gray-400 dark:border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-gray-900 dark:text-white text-xl md:text-2xl font-light text-center">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4">
          <LanguageSelector
            onLanguageSelect={handleLanguageSelect}
            selectedLanguage={selectedLanguage || undefined}
            className="w-full"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
